import { AgGridModule } from '@ag-grid-community/angular';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import {
  ColDef,
  ColGroupDef,
  GridOptions,
  ModuleRegistry,
} from '@ag-grid-community/core';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTimeSettings, UserRecord, Vendors } from '@cdw-ae/common-data';
import { MaterialLibraryModule } from '@cdw-ae/common-frontend-materials';
import { CdwDividerModule } from '@cdw-ae/common-ui-components-divider';
import { CdwSlideToggleModule } from '@cdw-ae/common-ui-components-slide-toggle';
import { DateTimeAngularService } from '@cdw-ae/date-time-angular-service';
import {
  FilterBarDialogComponent,
  FilterBarModule,
  MatcherComponentRegistryService,
} from '@cdw-ae/filter-bar-core';
import {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';
import {
  FilterBarSelectionMatchersModule,
  OuTreePickerMatcherComponent,
} from '@cdw-ae/filter-bar-selection-matchers';
import { FilterBarTemporalMatchersModule } from '@cdw-ae/filter-bar-temporal-matchers';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import * as AppSettingsActions from '../../../../../+state/app-settings.actions';
import * as FilterActions from '../../../../../+state/filter/filter.actions';
import {
  selectIsLoading,
  selectSelectedFilters,
} from '../../../../../+state/filter/filter.selectors';
import { selectCustomerDateTimeSettings } from '../../../../../+state/user-identity/user-identity-settings.selectors';
import { APP_NAME } from '../../../../../common/constants/app.constants';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import { RootState } from '../../../../../common/interfaces/rootState.interface';
import { GoogleTagManagerService } from '../../../../../common/services/analytics/google-tag-manager.service';
import { DevicesDataService } from '../../../../../common/services/device/devices-data.service';
import { SpogGaugeChartComponent } from '../../../../../components/charts/spog-gauge-chart/spog-gauge-chart.component';
import { REPORT_METADATA } from '../report-configs';
import { BaseReportHandler } from '../report-handlers/base-report.handler';
import { BatteryHealthReportHandler } from '../report-handlers/battery-health-report.handler';
import { ChromeVersionAgeReportHandler } from '../report-handlers/chrome-version-age-report.handler';
import { FifteenDayActivityReportHandler } from '../report-handlers/fifteen-day-activity-report.handler';
import { ModelAueReportHandler } from '../report-handlers/model-aue-report.handler';
import { StorageByOuReportHandler } from '../report-handlers/storage-by-ou-report.handler';
import { UserReportHandler } from '../report-handlers/user-report.handler';
import { REPORT_TYPES } from '../reports.model';
import {
  GaugeSummaryConfig,
  ReportData,
  ReportType,
  UserDeviceSession,
} from './report-viewer.model';

// Set AG Grid Enterprise license
LicenseManager.setLicenseKey(
  'Using_this_AG_Grid_Enterprise_key_( AG-041717 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Amplified It LLC )_is_granted_a_( Multiple Applications )_Developer_License_for_( 7 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_( 2 )_Production_Environments___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 9 February 2023 )____[v2]_MTY3NTkwMDgwMDAwMA==fde3befd306bf03b02dc772150bedb3c'
);

// Register AG Grid modules including row grouping
ModuleRegistry.registerModules([ClientSideRowModelModule, RowGroupingModule]);

@Component({
  selector: 'report-viewer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CdwDividerModule,
    CdwSlideToggleModule,
    AgGridModule,
    MaterialLibraryModule,
    FilterBarModule,
    FilterBarSelectionMatchersModule,
    FilterBarTemporalMatchersModule,
    FilterBarTextMatchersModule,
    SpogGaugeChartComponent,
  ],
  templateUrl: './report-viewer.component.html',
  styleUrls: ['./report-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReportViewerComponent implements OnInit, OnDestroy {
  public reportData?: ReportData;
  private store = inject(Store<RootState>);
  private devicesDataService = inject(DevicesDataService);
  private dateTimeService = inject(DateTimeAngularService);
  private titleService = inject(Title);
  private snackbarService = inject(SnackbarService);
  private gtmService = inject(GoogleTagManagerService);

  // AUE Report Toggle (Relative Time vs Fixed Time views)
  public aueViewModeControl = new FormControl<boolean>(false); // false = Relative, true = Fixed
  private activeReportSubscription: Subscription | null = null;

  // Tailwind colors for toggle styling
  protected readonly toggleTrackBg = TailwindColors.gray300;
  protected readonly tertiaryBlueCta = TailwindColors.tertiaryBlueCta;
  protected readonly primaryWhite = TailwindColors.primaryWhite;
  protected readonly primaryBlack = TailwindColors.primaryBlack;

  // Filter bar ID for NgRx state management
  private readonly reportFilterBarId = 'reportFilters';

  // Report handler for managing report-specific logic
  private reportHandler: BaseReportHandler | null = null;

  // Track current report type for reloading when filters change
  private currentReportType: string | null = null;

  // Raw API response cache for MODEL_AUE. Populated on first load, cleared on filter change.
  // Allows the viewMode toggle to re-transform without a new HTTP call.
  private cachedAueRawData: unknown[] | null = null;

  // Track if we've emitted initial filters to avoid duplicate emissions
  private hasEmittedInitialFilters = false;

  // Loading states
  public isLoadingSessions = false;
  public isLoadingReport = false;
  private _reportTitle = '';

  public get reportTitle(): string {
    return this._reportTitle;
  }

  public set reportTitle(value: string) {
    this._reportTitle = value;
    this.titleService.setTitle(`${value} - ${APP_NAME}`);
    this.store.dispatch(
      AppSettingsActions.updateHeaderText({ headerText: value })
    );
  }

  // Date/time settings for formatting
  private currentDateTimeSettings!: DateTimeSettings;
  private destroy$ = new Subject<void>();

  // AG Grid properties
  public deviceSessions: UserDeviceSession[] = [];
  public aggregateReportData: unknown[] = [];
  public filteredAggregateData: unknown[] = [];
  public gridOptions: GridOptions = {};
  public columnDefs: (ColDef | ColGroupDef)[] = [];

  // Filter bar properties for aggregate reports
  public aggregateFilterBarSettings: FilterBarField[] = [];
  private aggregateInitialFiltersSubject = new ReplaySubject<
    SelectedFilterField[]
  >(1);
  public aggregateInitialFilters$: Observable<SelectedFilterField[]> =
    this.aggregateInitialFiltersSubject.asObservable();
  public filterBarDialogComponent = FilterBarDialogComponent;

  // Gauge summary configuration (for reports with gauge cards)
  public gaugeSummaryConfig?: GaugeSummaryConfig;

  public get isUserReport(): boolean {
    return this.reportData?.type === 'SINGLE_USER';
  }

  public get isAggregateReport(): boolean {
    return !this.isUserReport && !!this.reportData?.aggregateConfig;
  }

  public get isChromeVersionAgeReport(): boolean {
    return this.reportData?.type === REPORT_TYPES.CHROME_VERSION_AGE;
  }

  public get isModelAueReport(): boolean {
    return this.reportData?.type === REPORT_TYPES.MODEL_AUE;
  }

  public get userData(): UserRecord | undefined {
    return this.isUserReport
      ? (this.reportData?.data as UserRecord)
      : undefined;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    matcherRegistry: MatcherComponentRegistryService
  ) {
    matcherRegistry.registerComponent(
      'ouTreePicker',
      OuTreePickerMatcherComponent
    );
    // Subscribe to date/time settings
    this.store
      .select(selectCustomerDateTimeSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.currentDateTimeSettings = settings;
      });

    // Subscribe to AUE view mode toggle changes
    this.aueViewModeControl.valueChanges
      .pipe(
        debounceTime(300), // Prevent race conditions on rapid toggle clicks
        withLatestFrom(
          this.store.select(selectSelectedFilters(this.reportFilterBarId))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([isFixedTime, currentFilters]) => {
        if (!this.isModelAueReport) return;

        const viewMode = isFixedTime ? 'fixed_time' : 'relative_time';

        // Persist toggle state (survives refresh, clears on logout)
        try {
          sessionStorage.setItem('aueView', viewMode);
        } catch {
          // biome-ignore lint/suspicious/noConsole: storage failure must be visible without leaking error details
          console.error('Failed to persist AUE view mode');
        }

        // Track toggle interaction in GTM
        this.gtmService.pushUserAction({
          category: 'reports',
          action: 'toggle_aue_view',
          metadata: {},
        });

        // Show loading state and reload report with new view mode
        if (this.currentReportType) {
          this.isLoadingReport = true;
          this.loadReportData(this.currentReportType, currentFilters, viewMode);
        }
      });
  }

  ngOnInit(): void {
    // Load filters from URL query params whenever they change
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: Record<string, string>) => {
        this.store.dispatch(
          FilterActions.loadFiltersFromUrl({ queryParams: params })
        );
      });

    // Read report type from URL parameter
    const reportType = this.route.snapshot.paramMap.get('reportType');

    // Restore AUE toggle state from sessionStorage for Model/AUE report
    if (reportType === REPORT_TYPES.MODEL_AUE) {
      let savedAueView: string | null = null;
      try {
        savedAueView = sessionStorage.getItem('aueView');
      } catch {
        // Storage blocked (e.g. Safari private mode) — fall back to relative_time.
      }
      if (savedAueView === 'fixed_time') {
        this.aueViewModeControl.setValue(true, { emitEvent: false });
      }
    }

    // Get report data from router navigation state (for user reports with pre-populated data)
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state ?? window.history.state;

    if (state?.reportData) {
      this.reportTitle =
        REPORT_METADATA[reportType as keyof typeof REPORT_METADATA]?.title ||
        state.reportTitle ||
        'User Report';
      this.reportData = state.reportData;

      if (this.reportData) {
        this.reportHandler = this.createReportHandler(this.reportData.type);

        if (this.isUserReport) {
          this.setupUserReport();
          this.loadDeviceSessions();
        }
      }
      return;
    }

    if (reportType) {
      this.reportTitle =
        REPORT_METADATA[reportType as keyof typeof REPORT_METADATA]?.title ||
        'Loading Report...';

      this.currentReportType = reportType;
      this.setupFilterBar(reportType);
      return;
    }
  }

  /**
   * Set up filter bar for a report type
   * 1. Subscribe to selectedFilters$ from NgRx store
   * 2. Emit to aggregateInitialFiltersSubject for filter bar display
   * 3. Let filter bar's filtersChange event trigger data loading via onAggregateFiltersChange
   */
  private setupFilterBar(reportType: string): void {
    const ReportHandler = this.getHandlerClass(reportType);

    // Create a temporary placeholder report data so the template container renders
    const tempReportData: ReportData = {
      type: reportType as ReportType,
      data: [],
      aggregateConfig: {
        columns: [],
        groupFields: [],
        aggregationField: '',
        data: [],
      },
    };

    // Set placeholder reportData so the template container renders
    this.reportData = tempReportData;

    // Get filter settings from static method (no instance needed)
    const filterSettings = ReportHandler.getFilterBarSettings();

    if (filterSettings) {
      this.aggregateFilterBarSettings = filterSettings;

      // Wait for URL filters to be loaded before emitting to filter bar
      // This prevents race condition where we emit empty array before URL is parsed
      combineLatest([
        this.store.select(selectSelectedFilters(this.reportFilterBarId)),
        this.store.select(selectIsLoading),
      ])
        .pipe(
          // Only proceed when filters are NOT being loaded (URL has been parsed)
          filter(([_, isLoading]) => !isLoading),
          distinctUntilChanged(([prevFilters], [currFilters]) =>
            isEqual(prevFilters, currFilters)
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(([filters, _]) => {
          // Default filters are ONLY set by NavigationConfig when navigating from reports page
          if (!this.hasEmittedInitialFilters) {
            this.hasEmittedInitialFilters = true;
            this.aggregateInitialFiltersSubject.next(filters);

            // Trigger the initial data load directly. Previously the embedded
            const viewMode = this.getInitialViewMode(reportType);
            this.loadReportData(reportType, filters, viewMode);
          }
        });
    } else {
      // No filter bar for this report - load data immediately with no filters
      const viewMode = this.getInitialViewMode(reportType);
      this.loadReportData(reportType, [], viewMode);
    }
  }

  private getInitialViewMode(
    reportType: string
  ): 'relative_time' | 'fixed_time' | undefined {
    if (reportType !== REPORT_TYPES.MODEL_AUE) {
      return undefined;
    }
    let savedAueView: string | null = null;
    try {
      savedAueView = sessionStorage.getItem('aueView');
    } catch {
      // Storage blocked (e.g. Safari private mode) — fall back to relative_time.
    }
    return (savedAueView === 'fixed_time' ? 'fixed_time' : 'relative_time') as
      | 'relative_time'
      | 'fixed_time';
  }

  private getHandlerClass(reportType: string) {
    switch (reportType) {
      case REPORT_TYPES.MODEL_AUE:
        return ModelAueReportHandler;
      case REPORT_TYPES.CHROME_VERSION_AGE:
        return ChromeVersionAgeReportHandler;
      case REPORT_TYPES.BATTERY_HEALTH:
        return BatteryHealthReportHandler;
      case REPORT_TYPES.STORAGE_BY_OU:
        return StorageByOuReportHandler;
      case REPORT_TYPES.FIFTEEN_DAY_ACTIVITY:
        return FifteenDayActivityReportHandler;
      case REPORT_TYPES.SINGLE_USER:
        return UserReportHandler;
      default:
        throw new Error(`Unknown report type: ${reportType}`);
    }
  }

  /**
   * Load report data based on report type
   * This is called when navigating with reportType instead of reportData
   * Delegates to the appropriate handler's static load method
   * @param reportType The type of report to load
   * @param filters Filters to apply to the API call
   */
  private loadReportData(
    reportType: string,
    filters: SelectedFilterField[],
    viewMode?: 'relative_time' | 'fixed_time'
  ): void {
    this.activeReportSubscription?.unsubscribe();
    this.activeReportSubscription = null;
    this.isLoadingReport = true;

    // Set report title from registry if not already set
    if (!this.reportTitle || this.reportTitle === 'Loading Report...') {
      this.reportTitle =
        REPORT_METADATA[reportType as keyof typeof REPORT_METADATA]?.title ||
        reportType;
    }

    const ReportHandler = this.getHandlerClass(reportType);

    // Pass viewMode to Model/AUE report handler.
    // aueCache is a shared object: load() reads it for cache-hit and writes it on fetch.
    const aueCache = { data: this.cachedAueRawData };
    const reportData$ =
      reportType === REPORT_TYPES.MODEL_AUE && viewMode
        ? (ReportHandler as typeof ModelAueReportHandler).load(
            this.devicesDataService,
            filters,
            viewMode,
            aueCache
          )
        : ReportHandler.load(this.devicesDataService, filters);

    // Subscribe to the report data observable and setup the report
    this.activeReportSubscription = reportData$.pipe(take(1)).subscribe({
      next: (reportData: ReportData) => {
        if (reportType === REPORT_TYPES.MODEL_AUE) {
          this.cachedAueRawData = aueCache.data;
        }
        this.reportData = reportData;
        this.reportHandler = new ReportHandler(reportData);

        if (this.reportData?.aggregateConfig) {
          this.setupAggregateReport();
        }

        this.isLoadingReport = false;
      },
      error: (err: unknown) => {
        this.isLoadingReport = false;
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        this.snackbarService.showError(
          `Failed to load report: ${message}`,
          'Close'
        );
        // biome-ignore lint/suspicious/noConsole: Error logging for API failure visibility
        console.error('Report load failed:', err);
      },
    });
  }

  /**
   * Factory method to create the appropriate report handler
   */
  private createReportHandler(type: string): BaseReportHandler {
    if (!this.reportData) {
      throw new Error('Report data is required to create handler');
    }

    const ReportHandler = this.getHandlerClass(type);
    return new ReportHandler(this.reportData);
  }

  /**
   * Setup user report using handler
   */
  private setupUserReport(): void {
    if (!this.reportHandler) return;

    this.columnDefs = this.reportHandler.getColumnDefs();
    this.gridOptions = this.reportHandler.getGridOptions();
  }

  /**
   * Setup aggregate report using handler
   */
  private setupAggregateReport(): void {
    if (!this.reportHandler) return;

    // Initialize handler with data (lifecycle hook for data-dependent setup)
    const aggregateData = this.reportData?.aggregateConfig?.data || [];
    this.reportHandler.initializeWithData(aggregateData);

    // Get configuration from handler
    this.columnDefs = this.reportHandler.getColumnDefs();

    // Apply date formatters to date columns
    this.applyDateFormatters();

    this.gridOptions = this.reportHandler.getGridOptions();

    // Set aggregate data
    this.aggregateReportData = this.reportData?.aggregateConfig?.data || [];
    this.filteredAggregateData = [...this.aggregateReportData];

    // Get gauge summary config with data if report supports it
    this.gaugeSummaryConfig =
      this.reportHandler.getGaugeSummaryConfig(this.filteredAggregateData) ??
      undefined;
  }

  /**
   * Apply date formatters to columns that contain date fields
   */
  private applyDateFormatters(): void {
    // Date field patterns to detect date columns
    const dateFieldPatterns = ['date', 'Date', 'TIME', 'time'];
    // Fields that should only show date (no time)
    const dateOnlyFields = ['releaseDate'];

    this.columnDefs = this.columnDefs.map((colDef) => {
      // Check if it's a regular column (not a column group)
      if ('field' in colDef && colDef.field) {
        const fieldName = colDef.field;

        // Check if field name suggests it's a date field
        const isDateField = dateFieldPatterns.some((pattern) =>
          fieldName.includes(pattern)
        );

        if (isDateField && !colDef.valueFormatter) {
          // Check if this field should only show date (no time)
          const isDateOnly = dateOnlyFields.includes(fieldName);

          return {
            ...colDef,
            valueFormatter: (params) => {
              // Special handling for releaseDate in Chrome Version Age report
              // Only show release date at level 0 (Chrome version nodes)
              if (fieldName === 'releaseDate' && params.node) {
                // Only display for level 0 (both group and leaf nodes)
                if (params.node.level !== 0) {
                  return '';
                }
                // Show N/A for null values at level 0
                if (!params.value) return 'N/A';
              }

              if (!params.value) return '';

              // Use formatDate for date-only fields, formatForGrid for date+time fields
              if (isDateOnly) {
                return this.dateTimeService.formatDate(
                  params.value,
                  this.currentDateTimeSettings
                );
              }

              return this.dateTimeService.formatForGrid(
                params.value,
                this.currentDateTimeSettings
              );
            },
          };
        }
      }

      return colDef;
    });
  }

  public onAggregateFiltersChange(filters: SelectedFilterField[]): void {
    this.cachedAueRawData = null; // Invalidate cache — filters changed, need fresh fetch
    // ALWAYS update the filters in URL (this will trigger the URL update effect)
    // Matches device search pattern exactly
    this.store.dispatch(
      FilterActions.updateFilters({
        filters,
        filterBarId: this.reportFilterBarId,
      })
    );

    // Load report data with the new filters, preserving the current view mode.
    // Read from the control value (not sessionStorage) — during the debounce window
    // after a toggle, sessionStorage may not yet reflect the latest control state.
    if (this.currentReportType) {
      const viewMode: 'relative_time' | 'fixed_time' | undefined =
        this.currentReportType === REPORT_TYPES.MODEL_AUE
          ? this.aueViewModeControl.value
            ? 'fixed_time'
            : 'relative_time'
          : undefined;
      this.loadReportData(this.currentReportType, filters, viewMode);
    }
  }

  private loadDeviceSessions(): void {
    if (!this.userData?.email) {
      return;
    }

    // Set loading state
    this.isLoadingSessions = true;

    // Fetch user sessions directly by email using consolidated endpoint
    // This consolidates: 1) Google User ID lookup, 2) BigQuery session fetch
    this.devicesDataService
      .getUserSessionsByEmail(this.userData.email)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (userSessions) => {
          if (!userSessions || userSessions.length === 0) {
            this.deviceSessions = [];
            this.isLoadingSessions = false;
            return;
          }

          // Sessions carry Google's device ID, which SPOG stores as vendorId.
          // One request covers every device in the report.
          const vendorIds = [
            ...new Set(
              userSessions
                .map((session) => session.deviceId as string)
                .filter((id): id is string => !!id)
            ),
          ];

          const devices$ = this.devicesDataService
            .getDevicesByVendorIds(vendorIds)
            .pipe(take(1), takeUntil(this.destroy$));

          devices$.subscribe({
            next: (devices) => {
              const allSessions: UserDeviceSession[] = [];

              userSessions.forEach((session: Record<string, unknown>) => {
                const device = devices.find(
                  (d) => d?.vendorId === (session.deviceId as string)
                );
                const googleDevice = device?.vendorProperties?.[Vendors.GOOGLE];

                // Format session start time
                let formattedSessionStart = 'N/A';
                if (session.startTime) {
                  try {
                    formattedSessionStart = this.dateTimeService.format(
                      session.startTime as string,
                      this.currentDateTimeSettings
                    );
                  } catch {
                    formattedSessionStart =
                      (session.startTime as string) || 'N/A';
                  }
                }

                allSessions.push({
                  hierarchy: [],
                  serialNumber: device?.serialNumber || 'N/A',
                  sessionStart: formattedSessionStart,
                  // Gopher Buddy already reports duration in minutes
                  sessionDuration: session.duration
                    ? `${session.duration as number} min`
                    : 'N/A',
                  sessionPrivateIp: (session.privateIp as string) || 'N/A',
                  sessionPublicIp: (session.publicIp as string) || 'N/A',
                  osVersion:
                    (session.crosVersion as string) ||
                    (googleDevice?.osVersion as string) ||
                    'N/A',
                  deviceOuPath:
                    (session.deviceOrgUnitPath as string) ||
                    (googleDevice?.orgUnitPath as string) ||
                    'N/A',
                  recentnessOfUse: 'Coming Soon...',
                  ouPath: (googleDevice?.orgUnitPath as string) || 'N/A',
                  annotatedUser:
                    (googleDevice?.annotatedUser as string) ||
                    (session.user as string) ||
                    this.userData?.email ||
                    'N/A',
                  annotatedLocation:
                    (googleDevice?.annotatedLocation as string) || 'N/A',
                  // Detail fields
                  lastSync: googleDevice?.lastSync as Date,
                  model: device?.model,
                  status: (googleDevice?.status as string) || 'N/A',
                  deviceId: device?.id || '',
                });
              });

              this.deviceSessions = allSessions;
              this.isLoadingSessions = false;
            },
            error: () => {
              this.deviceSessions = [];
              this.isLoadingSessions = false;
            },
          });
        },
        error: () => {
          this.isLoadingSessions = false;
          this.deviceSessions = [];
        },
      });
  }

  public get givenName(): string {
    if (!this.userData?.name) return 'Coming Soon...';
    const nameParts = this.userData.name.trim().split(' ');
    return nameParts[0] || 'Coming Soon...';
  }

  public get familyName(): string {
    if (!this.userData?.name) return 'Coming Soon...';
    const nameParts = this.userData.name.trim().split(' ');
    return nameParts.length > 1
      ? nameParts.slice(1).join(' ')
      : 'Coming Soon...';
  }

  public formatDate(date: Date | string | undefined): string | undefined {
    if (!date) return undefined;
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString();
  }

  public goBackToReports(): void {
    this.router.navigate(['/reports']);
  }

  ngOnDestroy(): void {
    this.activeReportSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
